// Wholesale Closing Crash Course — landing page for the assignment-contract
// lead magnet. Captures email/name → POST /api/lead-magnet/assignment-contract
// → enrolls visitor in the 5-day "closing the deal" follow-up drip.
//
// Design language mirrors /free-ebook (Call Coach) for brand consistency
// but with copy tuned to the contract / closing workflow.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { track, captureUtm } from "@/lib/analytics";
import AssignFlowLogo from "@/components/AssignFlowLogo";
import {
  ArrowRight, Mail, User as UserIcon, Download, CheckCircle2,
  FileSignature, Scale, ShieldCheck, Sparkles, Lock, BookOpen,
} from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${backend}/api/lead-magnet/assignment-contract.pdf`;
const CAPTURE_URL = `${backend}/api/lead-magnet/assignment-contract`;

const CHAPTERS = [
  { n: "01", t: "The two-page contract every wholesaler needs (with annotations)" },
  { n: "02", t: "Assignment vs. double-close — when each protects you" },
  { n: "03", t: "The five clauses that kill 80% of deals" },
  { n: "04", t: "Earnest money: how much, when, and where it sits" },
  { n: "05", t: "The 'and/or assigns' clause and why it matters" },
  { n: "06", t: "Title company disputes — three scripts that get yes" },
  { n: "07", t: "Reasonable inspection windows for fast closings" },
  { n: "08", t: "How to handle a seller who wants to back out" },
  { n: "09", t: "Real closing example — 11 days from contract to wire" },
  { n: "10", t: "Your closing-day checklist (single-page printable)" },
];

const PROOF_POINTS = [
  { k: "22 pp",     v: "of contract templates & playbooks" },
  { k: "5 emails",  v: "follow-up closing series" },
  { k: "1 deal",    v: "covers it: zero cost, all value" },
];


export default function WholesaleCrashCourse() {
  useEffect(() => {
    captureUtm();
    track("contract_ebook_view");
  }, []);
  return (
    <div className="mkt-shell bg-[#F8FAFC] min-h-screen">
      <EbookNav />
      <Hero />
      <ProofStrip />
      <Inside />
      <FinalCTA />
      <Footer />
    </div>
  );
}


function EbookNav() {
  return (
    <header className="mkt-nav border-b border-[var(--line)]">
      <div className="mkt-nav-inner">
        <Link to="/" className="flex items-center gap-2" data-testid="contract-ebook-brand">
          <img src="/brand/wordmark-color.png" alt="iSpeedToClose" className="h-14 md:h-16 w-auto" width="243" height="64" />
        </Link>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/resources" className="btn-ghost" data-testid="contract-ebook-back-resources">
            ← All resources
          </Link>
          <Link to="/register" className="btn-money" data-testid="contract-ebook-nav-register">
            Try free <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </header>
  );
}


function Hero() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.email.includes("@") || form.name.trim().length < 1) {
      setErrorMsg("Please enter your name and a valid email.");
      return;
    }
    setStatus("submitting");
    try {
      const r = await fetch(CAPTURE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          utm: JSON.parse(sessionStorage.getItem("utm_seen") || "{}"),
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Couldn't submit.");
      setStatus("success");
      track("contract_ebook_capture");
    } catch (err) {
      setErrorMsg(err.message || "Couldn't submit.");
      setStatus("error");
    }
  };

  return (
    <section className="mkt-section relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-amber-300/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
      <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
        <div>
          <div className="mkt-eyebrow flex items-center gap-2">
            <FileSignature size={12} /> Free Ebook · 22 Pages
          </div>
          <h1 className="font-display font-black tracking-tighter mt-3 text-5xl lg:text-6xl leading-[0.96]">
            The Wholesale Closing Crash Course
          </h1>
          <p className="text-lg text-[var(--ink-2)] mt-4 max-w-xl leading-relaxed">
            The exact contracts, clauses, and closing-day plays we use to take wholesale deals from
            handshake to wire transfer in <strong>under 21 days</strong>. Includes a two-page assignment
            contract template you can use today.
          </p>

          <form onSubmit={submit} className="mt-7 max-w-md" data-testid="contract-ebook-form">
            <div className="flex flex-col sm:flex-row gap-2">
              <label className="relative flex-1">
                <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                <input
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input pl-9 w-full"
                  data-testid="contract-ebook-name"
                />
              </label>
              <label className="relative flex-1">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                <input
                  type="email"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input pl-9 w-full"
                  data-testid="contract-ebook-email"
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={status === "submitting" || status === "success"}
              className="btn-money mt-3 w-full justify-center"
              data-testid="contract-ebook-submit"
            >
              {status === "success" ? (
                <><CheckCircle2 size={16} /> Sent to your inbox</>
              ) : status === "submitting" ? (
                "Sending…"
              ) : (
                <>Send me the playbook <ArrowRight size={14} /></>
              )}
            </button>
            {status === "success" && (
              <a
                href={PDF_URL}
                target="_blank" rel="noreferrer"
                className="block mt-3 text-center text-sm font-semibold text-emerald-700 hover:underline"
                data-testid="contract-ebook-download-link"
              >
                <Download size={14} className="inline mr-1" /> Or download the PDF directly
              </a>
            )}
            {errorMsg && (
              <div className="text-rose-600 text-sm mt-2" data-testid="contract-ebook-error">
                {errorMsg}
              </div>
            )}
            <div className="text-[11px] text-[var(--ink-3)] mt-3 flex items-center gap-1.5">
              <Lock size={11} /> Your email goes to the playbook drip — unsubscribe anytime.
            </div>
          </form>
        </div>

        {/* Cover mock */}
        <div className="relative">
          <div className="rounded-3xl bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white p-8 lg:p-10 shadow-2xl border border-white/10 max-w-md mx-auto aspect-[3/4]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--primary)] font-bold">
              iSpeedToClose
            </div>
            <h2 className="font-display font-black text-3xl tracking-tight mt-4">
              Wholesale Closing Crash Course
            </h2>
            <div className="text-sm text-white/70 mt-2">
              The contracts, clauses & closing-day plays
            </div>
            <div className="absolute bottom-8 left-8 right-8 flex items-center gap-2 text-xs text-white/60">
              <FileSignature size={14} className="text-[var(--primary)]" />
              22 pages · Annotated · Free
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20 text-[var(--primary)]">
              <Scale size={120} strokeWidth={1} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function ProofStrip() {
  return (
    <section className="mkt-section" style={{ paddingTop: 0 }}>
      <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
        {PROOF_POINTS.map((p) => (
          <div key={p.k} className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 text-center">
            <div className="font-display font-black text-3xl text-amber-700 whitespace-pre-line">{p.k}</div>
            <div className="text-xs text-[var(--ink-2)] mt-1">{p.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}


function Inside() {
  return (
    <section className="mkt-section">
      <div className="text-center mb-10">
        <div className="mkt-eyebrow"><BookOpen size={11} className="inline mr-1" /> What's Inside</div>
        <h2 className="font-display font-black text-4xl tracking-tighter mt-2">
          10 chapters. Every contract clause that matters.
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
        {CHAPTERS.map((c) => (
          <div key={c.n} className="flex items-start gap-3 p-3 rounded-lg border border-[var(--line)] bg-white">
            <div className="font-display font-black text-xl text-[var(--primary)] shrink-0">{c.n}</div>
            <div className="text-sm text-[var(--ink)] leading-snug pt-0.5">{c.t}</div>
          </div>
        ))}
      </div>
    </section>
  );
}


function FinalCTA() {
  return (
    <section className="mkt-section">
      <div className="rounded-3xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white p-10 lg:p-14 text-center max-w-4xl mx-auto">
        <Sparkles size={20} className="text-[var(--primary)] mx-auto mb-3" />
        <h2 className="font-display font-black text-4xl tracking-tight">
          Stop losing deals to messy paperwork.
        </h2>
        <p className="text-white/70 mt-3 max-w-xl mx-auto">
          Drop your email above to get the 22-page crash course delivered to your inbox in 30 seconds.
        </p>
        <Link to="/register" className="inline-flex items-center gap-2 bg-[var(--primary)] text-white font-bold px-7 py-3 rounded-xl mt-6 hover:opacity-90 transition" data-testid="contract-ebook-cta-register">
          <ShieldCheck size={16} /> Want the whole platform free? Try iSpeedToClose
        </Link>
      </div>
    </section>
  );
}


function Footer() {
  return (
    <footer className="mkt-section text-center text-xs text-[var(--ink-3)] pb-12">
      © {new Date().getFullYear()} iSpeedToClose · <Link to="/resources" className="hover:underline">All Free Resources</Link>
    </footer>
  );
}
